import { NOOP } from '@egw/constants'
import { InformationCircleIcon as InfoIconOutline } from '@heroicons/react/24/outline'
import { InformationCircleIcon as InfoIconFilled } from '@heroicons/react/24/solid'
import cx from 'classnames'
import { useEffect, useRef, useState, type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'
import { ModalDrawer } from '../modal-drawer/modal-drawer'
import { useIsMobileScreen } from './hooks/layout-hook'

export interface PopoverProps extends DefaultProps {
  /**
   * Popover title
   */
  title: string

  /**
   * Used to override title for a custom popover appearance
   */
  display?: JSX.Element

  /**
   * Popover Icon Classnames
   */
  iconClassnames?: string

  /**
   * Popover Classnames
   */
  popoverClassnames?: string

  /**
   * Side where the popover appears: 'left', 'right', 'top', 'bottom'
   */
  side?: 'left' | 'right' | 'top' | 'bottom'
}

export const Popover: FC<PopoverProps> = ({
  children,
  title,
  display,
  iconClassnames,
  popoverClassnames,
  id,
  testId,
  ariaLabel,
  side = 'right',
}) => {
  const isMobileScreen = useIsMobileScreen()

  const [show, setShow] = useState<boolean>(false)

  const [showModalForMobile, setShowModalForMobile] = useState<boolean>(false)

  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const handleMouseOver = (): void => {
    // For Desktop screens on hover show the tooltip
    if (!isMobileScreen) {
      setShow(true)
    }
  }

  const handleMouseLeft = (): void => {
    // For Desktop screens on mouse left hide the tooltip
    if (!isMobileScreen) {
      setShow(false)
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (wrapperRef.current instanceof HTMLDivElement && !wrapperRef.current.contains(event.target as Node)) {
        setShow(false)
      }
    }

    if (show) {
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [show, wrapperRef])

  const openHandler = (): void => {
    // Mobile Screens show Popover Content in Modal
    if (isMobileScreen) {
      setShowModalForMobile(true)
    } else NOOP
  }

  let positionClasses = ''
  let arrowClasses = ''

  if (isMobileScreen) {
    positionClasses = 'top-[100%] right-[25%]'
  } else {
    switch (side) {
      case 'left':
        positionClasses = 'right-full right-xl top-1/2 -translate-y-1/2'
        arrowClasses = 'absolute right-[-6px] top-1/2 transform -translate-y-1/2 w-3 h-3 bg-base-black rotate-45'
        break
      case 'right':
        positionClasses = 'left-full left-xl top-1/2 -translate-y-1/2'
        arrowClasses = 'absolute left-[-6px] top-1/2 transform -translate-y-1/2 w-3 h-3 bg-base-black rotate-45'
        break
      case 'top':
        positionClasses = 'bottom-full bottom-lg left-1/2 -translate-x-1/2'
        arrowClasses = 'absolute bottom-[6px] left-1/2 transform -translate-x-1/2 w-3 h-3 bg-base-black rotate-45'
        break
      case 'bottom':
        positionClasses = 'top-full top-xl left-1/2 -translate-x-1/2'
        arrowClasses = 'absolute top-[-6px] left-1/2 transform -translate-x-1/2 w-3 h-3 bg-base-black rotate-45'
        break
      default:
        positionClasses = 'left-full left-xl top-1/2 -translate-y-1/2'
        arrowClasses = 'absolute left-[6px] top-1/2 transform -translate-y-1/2 w-3 h-3 bg-base-black rotate-45'
    }
  }

  const iconFilledClassnames = twMerge('flex-shrink-0 w-6 h-6 text-icons-full hidden group-hover:block', iconClassnames)
  const iconOutlineClassnames = twMerge(
    'flex-shrink-0 w-6 h-6 text-icons-full block group-hover:hidden',
    iconClassnames,
  )

  return (
    <>
      <div
        {...(testId ? { 'data-testid': testId } : {})}
        {...(id ? { id } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        ref={wrapperRef}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseLeft}
        className="w-fit h-fit relative flex justify-center cursor-pointer"
      >
        {display ? (
          <button type="button" onClick={openHandler} id="openPopoverCta">
            {display}
          </button>
        ) : (
          <button name="info-button" type="button" id="popoverFilled" onClick={openHandler} className="group">
            <div>
              <InfoIconFilled className={iconFilledClassnames} />
              <InfoIconOutline className={iconOutlineClassnames} />
            </div>
          </button>
        )}

        {/* Poppover Content */}
        <div hidden={!show} className={`min-w-fit w-[250px] h-fit absolute z-50 transition-all ${positionClasses}`}>
          {arrowClasses ? <div className={arrowClasses} /> : null}

          <div
            data-testid="popoverContentWrapper"
            className={cx('rounded bg-base-black shadow-lg mb-[10px] p-sm flex flex-col gap-1', popoverClassnames)}
          >
            <div className="typo-primary-base-semibold text-text-reverse">{title}</div>
            <div className="typo-primary-sm-regular text-text-reverse">{children}</div>
          </div>
        </div>
      </div>
      <ModalDrawer
        className="popoverModal"
        title={title}
        isOpen={showModalForMobile}
        onClose={() => {
          setShowModalForMobile(false)
        }}
      >
        {children}
      </ModalDrawer>
    </>
  )
}
